import {
  DEFAULT_ELECTRICITY_PRICE_GROWTH_PERCENT,
  SELF_CONSUMPTION_FRACTION,
  SELF_CONSUMPTION_FRACTION_WITH_BATTERY,
  SOLAR_SAVINGS_HORIZON_YEARS,
  resolveSolarSavingsRates,
  type SolarSavingsRegion,
} from './solarSavingsConstants';

export type SolarSavings = {
  /**
   * Indicative annual bill savings in MAJOR units of the region's currency
   * (GBP for 'uk', USD for 'us'). Mapped onto GraphQL `estimatedAnnualSavings`.
   */
  annualSavingsGbp: number;
  /**
   * Indicative simple payback in years, from the given system cost (or the
   * per-panel indicative cost). Null when savings are zero (nothing to pay
   * back against).
   */
  paybackYears: number | null;
  /**
   * The share of generation the money was charged at the retail tariff,
   * 0..1. Returned so a caller that PRINTS the assumption prints the one the
   * figures were worked out from, whichever way it was chosen.
   */
  selfConsumptionFraction: number;
};

/**
 * The share of generation used in the home rather than exported, which is the
 * single assumption that separates a battery system's savings from a
 * panels-only one. Exported here so a caller that PRINTS the assumption reads
 * the same fraction {@link computeSavings} charged the money at, rather than
 * naming a constant and hoping the two agree.
 */
export const selfConsumptionFraction = (includeBattery: boolean): number =>
  includeBattery
    ? SELF_CONSUMPTION_FRACTION_WITH_BATTERY
    : SELF_CONSUMPTION_FRACTION;

/**
 * Which annual generation figure the money on a document is worked out from.
 *
 * MIS 3002 4.1.8(b): an additional estimate (our satellite-modelled yield)
 * may not be more prominent than the MCS standard estimate. Payback and bill
 * savings are the most prominent figures on the document, so wherever the
 * MCS block prints they follow the MCS total; without it (US accounts, and
 * UK estimates with no traced roof or no postcode zone) the satellite figure
 * is the only one there is.
 */
export const solarSavingsBasis = (args: {
  mcsAnnualOutputKwh: number | null;
  satelliteAnnualKwh: number;
}): { annualKwh: number; basis: 'mcs' | 'satellite' } =>
  args.mcsAnnualOutputKwh != null
    ? { annualKwh: args.mcsAnnualOutputKwh, basis: 'mcs' }
    : { annualKwh: args.satelliteAnnualKwh, basis: 'satellite' };

/**
 * The annual generation the money is worked out from, kWh at the AC side of
 * the inverter — the satellite yield after {@link SOLAR_DC_TO_AC_DERATE}, or
 * the MCS Appendix B total, which is AC already.
 */
export type SolarSavingsEnergyInput = { annualAcKwh: number };

/**
 * Indicative annual savings and payback for a candidate panel configuration,
 * using the account region's tariff assumptions.
 *
 * Savings = generation × (self-consumed fraction at the retail tariff +
 * exported remainder at the export rate). With a battery the self-consumed
 * fraction rises, because the evening is bought back off the battery instead
 * of off the grid. Payback divides the system cost by the annual savings;
 * `systemCostMajor` is used when the caller knows real appliance pricing
 * (the provider designer path), otherwise the region's indicative per-panel
 * cost applies.
 *
 * `includeBattery` is REQUIRED rather than defaulted, so a caller can never
 * silently price a battery system at the panels-only fraction by omitting
 * it. A caller with no battery to declare says `false` on purpose.
 *
 * `selfConsumptionFraction`, when given, replaces the fixed assumption with
 * a measured one: on a UK estimate that carries the MIS 3002 table, MGD 003
 * has already worked out how much of the generation this household keeps,
 * with and without its battery, and the document prints that figure in its
 * appendix. The money must be charged at the same share, or the same page
 * says 82% in one place and 65% in another. The fixed fractions remain the
 * fallback for estimates without household data and for non-UK regions.
 */
export const computeSavings = (
  args: SolarSavingsEnergyInput & {
    panelsCount: number;
    region: SolarSavingsRegion;
    includeBattery: boolean;
    /**
     * The share of generation used in the home, 0..1, where it has been
     * worked out for this household (MGD 003). Null or omitted = the fixed
     * assumption for `includeBattery`.
     */
    selfConsumptionFraction?: number | null;
    /** Actual selected system cost in the region currency's major units. */
    systemCostMajor?: number | null;
    /**
     * Account retail / export rates. Null fields use the region default.
     * Same shape as {@link resolveSolarSavingsRates}.
     */
    retailTariffMinorPerKwh?: number | null;
    exportTariffMinorPerKwh?: number | null;
  }
): SolarSavings => {
  const rates = resolveSolarSavingsRates(args.region, args);
  const fraction =
    args.selfConsumptionFraction != null
      ? Math.min(1, Math.max(0, args.selfConsumptionFraction))
      : selfConsumptionFraction(args.includeBattery);

  const minorSavedPerKwh =
    fraction * rates.retailTariffMinorPerKwh +
    (1 - fraction) * rates.exportTariffMinorPerKwh;

  const annualSavingsGbp = (args.annualAcKwh * minorSavedPerKwh) / 100;

  const systemCostMajor =
    args.systemCostMajor ??
    args.panelsCount * rates.indicativeCostPerPanelMajor;

  const paybackYears =
    annualSavingsGbp > 0 ? systemCostMajor / annualSavingsGbp : null;

  return { annualSavingsGbp, paybackYears, selfConsumptionFraction: fraction };
};

/** One year of the savings series, in MAJOR units of the region's currency. */
export type SolarSavingsYear = {
  /** 1 for the first year of generation. */
  year: number;
  /** Bill savings earned in this year alone. */
  savingsThatYear: number;
  /** Savings earned from year 1 to this year inclusive. */
  runningTotal: number;
  /** `runningTotal` less the system cost: negative until break-even. */
  balance: number;
};

export type SolarSavingsSeries = {
  years: SolarSavingsYear[];
  /** The running total at the end of the last year. */
  total: number;
  /**
   * First year whose balance is not negative, i.e. the year the savings have
   * covered the system cost. Null when the cost is never recovered inside
   * the horizon (a very shaded roof, or a system priced well above its yield).
   */
  breakEvenYear: number | null;
  /** The growth rate the series was built with, percent a year. */
  priceGrowthPercent: number;
  /** Annual panel output loss the series was built with, percent a year. */
  degradationPercent: number;
};

/**
 * The year-by-year savings and payback table: what the system saves each
 * year, what it has saved by then, and where that stands against the price
 * paid for it.
 *
 * Year one is the year-one saving as calculated elsewhere; each later year
 * grows it by `priceGrowthPercent` and, when `degradationPercent` is set,
 * shrinks generation by that rate. Export-rate drift and maintenance are
 * not modelled, and the document says so.
 *
 * Money stays in major units and unrounded here; the caller rounds and
 * formats, because a table that rounds every row and then sums the rounded
 * rows disagrees with itself.
 */
export const solarSavingsSeries = (args: {
  /** Year-one bill savings, major units (`computeSavings().annualSavingsGbp`). */
  annualSavingsMajor: number;
  /** The outlay to recover, major units. */
  systemCostMajor: number;
  /** Defaults to {@link SOLAR_SAVINGS_HORIZON_YEARS}. */
  years?: number;
  /** Defaults to {@link DEFAULT_ELECTRICITY_PRICE_GROWTH_PERCENT}. */
  priceGrowthPercent?: number;
  /** Annual panel output loss, percent. Defaults to 0 (output not aged). */
  degradationPercent?: number;
}): SolarSavingsSeries => {
  const {
    annualSavingsMajor,
    systemCostMajor,
    years = SOLAR_SAVINGS_HORIZON_YEARS,
    priceGrowthPercent = DEFAULT_ELECTRICITY_PRICE_GROWTH_PERCENT,
    degradationPercent = 0,
  } = args;

  const growth = 1 + priceGrowthPercent / 100;
  const remain = 1 - degradationPercent / 100;
  const series: SolarSavingsYear[] = [];
  let runningTotal = 0;
  let breakEvenYear: number | null = null;

  for (let year = 1; year <= years; year++) {
    const savingsThatYear =
      annualSavingsMajor * growth ** (year - 1) * remain ** (year - 1);
    runningTotal += savingsThatYear;
    const balance = runningTotal - systemCostMajor;
    if (breakEvenYear === null && balance >= 0) breakEvenYear = year;
    series.push({ year, savingsThatYear, runningTotal, balance });
  }

  return {
    years: series,
    total: runningTotal,
    breakEvenYear,
    priceGrowthPercent,
    degradationPercent,
  };
};
